/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

/* Global AJAX add-to-cart with loader-button support (upsells, custom ATC buttons). */
document.addEventListener('click', async function (e) {
  const btn = e.target.closest('button[data-ajax-cart-add][data-variant-id]');
  if (!btn) return;

  if (btn.hasAttribute('disabled') || btn.getAttribute('aria-busy') === 'true') return;

  e.preventDefault();

  const variantIdRaw = btn.getAttribute('data-variant-id');
  const variantId = Number.parseInt(variantIdRaw, 10);
  if (!Number.isFinite(variantId)) return;

  btn.setAttribute('disabled', 'disabled');
  btn.setAttribute('aria-busy', 'true');

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    });

    if (!res.ok) {
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return;
    }

    const cartRes = await fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
    const cart = await cartRes.json();

    document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: true } })
    );
  } catch (err) {
    console.error('AJAX add-to-cart failed:', err);
  } finally {
    btn.removeAttribute('disabled');
    btn.removeAttribute('aria-busy');
  }
});

/* Fallback loader handling for customized built-in elements.
   Some environments fail to upgrade `button is="loader-button"`, so we ensure
   `aria-busy` is toggled via event delegation. */
(function () {
  const busyAttr = 'aria-busy';

  function setBusy(button, value) {
    if (!button) return;
    if (value) button.setAttribute(busyAttr, 'true');
    else button.removeAttribute(busyAttr);
  }

  // On any click on a loader-button, show loader immediately.
  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest('button[is="loader-button"]');
      if (!btn) return;
      if (btn.hasAttribute('disabled')) return;
      // Only apply to submit buttons or explicit ajax cart add buttons
      if (btn.type === 'submit' || btn.matches('[data-ajax-cart-add]')) {
        setBusy(btn, true);
      }
    },
    true
  );

  // On form submit, ensure the submitter shows loader (covers Enter key submit).
  document.addEventListener(
    'submit',
    function (e) {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      const submitter = e.submitter;
      if (submitter && submitter.matches && submitter.matches('button[is="loader-button"]')) {
        setBusy(submitter, true);
      }
    },
    true
  );

  // Remove busy state after cart refresh events (theme already re-renders UI).
  document.documentElement.addEventListener(
    'cart:refresh',
    function () {
      document.querySelectorAll('button[is="loader-button"][aria-busy]').forEach((btn) => setBusy(btn, false));
    },
    true
  );

  // Safety: never leave loader stuck forever.
  document.addEventListener(
    'click',
    function (e) {
      const btn = e.target.closest('button[is="loader-button"]');
      if (!btn) return;
      if (btn.getAttribute(busyAttr) !== 'true') return;
      window.setTimeout(() => setBusy(btn, false), 8000);
    },
    true
  );
})();

/* Fake urgency (24h timer from midnight + random inventory) */
(function () {
  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function ymdLocal(d) {
    const y = d.getFullYear();
    const m = pad2(d.getMonth() + 1);
    const day = pad2(d.getDate());
    return `${y}-${m}-${day}`;
  }

  function stableDailyRandomInt(key, max) {
    if (!Number.isFinite(max) || max <= 0) return null;
    try {
      const existing = window.localStorage.getItem(key);
      if (existing != null) {
        const parsed = Number.parseInt(existing, 10);
        if (Number.isFinite(parsed) && parsed >= 1 && parsed <= max) return parsed;
      }
      const val = Math.max(1, Math.min(max, Math.floor(Math.random() * max) + 1));
      window.localStorage.setItem(key, String(val));
      return val;
    } catch {
      return Math.max(1, Math.min(max, Math.floor(Math.random() * max) + 1));
    }
  }

  function getRemainingToNextMidnightMs(now) {
    const end = new Date(now);
    end.setHours(24, 0, 0, 0); // next midnight local time
    return Math.max(0, end.getTime() - now.getTime());
  }

  function formatHMS(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return `${pad2(h)}:${pad2(m)}:${pad2(s)}`;
  }

  function initFakeUrgency(root) {
    const scope = root || document;
    const nodes = scope.querySelectorAll('[data-fake-urgency]:not([data-fake-urgency-initialized])');

    nodes.forEach((el) => {
      el.setAttribute('data-fake-urgency-initialized', 'true');

      const productId = el.getAttribute('data-product-id');
      const maxInv = Number.parseInt(el.getAttribute('data-max-inventory') || '0', 10);
      const showInventory = el.getAttribute('data-show-inventory') === 'true';
      const showTimerSetting = el.getAttribute('data-show-timer') === 'true';
      const timerEnabledForProduct = el.getAttribute('data-timer-enabled') === 'true';

      const invNode = el.querySelector('.fake-urgency__inventory');
      const invTextNode = el.querySelector('.fake-urgency__inventory-text');
      const timerWrap = el.querySelector('.fake-urgency__timer');
      const timerPrefixNode = el.querySelector('.fake-urgency__timer-prefix');
      const timerPrefixTextNode = el.querySelector('.fake-urgency__timer-prefix-text');
      const timerValueNode = el.querySelector('.fake-urgency__timer-value');

      if (showInventory && invNode && productId) {
        const key = `fakeUrgencyInv:${productId}:${ymdLocal(new Date())}`;
        const count = stableDailyRandomInt(key, maxInv || 63);
        const template = el.getAttribute('data-inventory-template') || 'Only {count} left';
        const inventoryText = template.replaceAll('{count}', String(count));
        if (invTextNode) invTextNode.textContent = inventoryText;
        else invNode.textContent = inventoryText;
        invNode.hidden = false;
      }

      if (showTimerSetting && timerEnabledForProduct && timerWrap && timerPrefixNode && timerValueNode) {
        const prefix = el.getAttribute('data-timer-prefix') || 'Offer ends in';
        if (timerPrefixTextNode) timerPrefixTextNode.textContent = prefix;
        else timerPrefixNode.textContent = prefix;
        timerWrap.hidden = false;

        const tick = () => {
          const now = new Date();
          const remaining = getRemainingToNextMidnightMs(now);
          timerValueNode.textContent = formatHMS(remaining);
        };

        tick();
        const intervalId = window.setInterval(tick, 1000);

        // Prevent orphan intervals if element is removed (instant navigation)
        const observer = new MutationObserver(() => {
          if (!document.documentElement.contains(el)) {
            window.clearInterval(intervalId);
            observer.disconnect();
          }
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initFakeUrgency(document));
  document.addEventListener('shopify:section:load', (e) => initFakeUrgency(e.target));
  document.addEventListener('shopify:section:reorder', (e) => initFakeUrgency(e.target));
})();